import type { TFunction } from 'i18next';
import {
  resolveQuestionnaireDisplayName,
  type QuestionnaireTemplateName,
} from '@/lib/questionnaireDisplayName';
import type { StaffCourseQuestionnaire, StaffCourseStep } from '@/lib/treatmentCourseStaffView';
import { deriveCurrentStepId } from '@/lib/courseStepStatus';
import { computeCourseIntervalWindow, type CourseIntervalWindow } from '@/lib/courseIntervalWindow';
import { practitionerDisplayName, previousBookedStartsAtForStep } from '@/lib/courseStepSlotPicker';
import { stepDisplayTitle } from '@/lib/bookedTreatmentCourse';
import type { CourseActionItem } from '@/lib/courseRequiresAction';
import { formatCourseDate, formatCourseDateTime } from '@/lib/courseDateFormat';

export function clinicMemberNameById(
  members:
    | readonly { user_id: string; name?: string | null; email?: string | null }[]
    | null
    | undefined,
): Map<string, string> {
  const map = new Map<string, string>();
  for (const member of members ?? []) {
    const label = practitionerDisplayName(member.name || member.email, '');
    if (label) map.set(member.user_id, label);
  }
  return map;
}

/** Display name for a clinic member / booked_by value. Never a UUID. */
export function resolveCoursePersonName(
  raw: string | null | undefined,
  names: ReadonlyMap<string, string> = new Map(),
): string | null {
  const trimmed = raw?.trim() ?? '';
  if (!trimmed) return null;
  return practitionerDisplayName(names.get(trimmed) ?? trimmed, '') || null;
}

export function coursePersonNames(
  people: readonly { name?: string | null; id?: string | null }[],
  names: ReadonlyMap<string, string> = new Map(),
): string {
  const seen = new Set<string>();
  const labels: string[] = [];
  for (const person of people) {
    const label = resolveCoursePersonName(person.name, names) ?? resolveCoursePersonName(person.id, names);
    if (!label || seen.has(label)) continue;
    seen.add(label);
    labels.push(label);
  }
  return labels.join(', ');
}

export function courseCreatedByClause(
  bookedBy: string | null | undefined,
  names: ReadonlyMap<string, string>,
  dateLabel: string,
  createdBy: (vars: { name: string; date: string }) => string,
): string {
  const name = resolveCoursePersonName(bookedBy, names);
  if (name) return createdBy({ name, date: dateLabel });
  return dateLabel;
}

export function courseQuestionnaireName(
  row: Pick<StaffCourseQuestionnaire, 'forms_template_id' | 'template_name'>,
  language: string,
  fallback: string,
  templatesById: Map<string, QuestionnaireTemplateName> | Record<string, QuestionnaireTemplateName> = {},
): string {
  return resolveQuestionnaireDisplayName({
    templateId: row.forms_template_id,
    snapshotName: row.template_name,
    locale: language,
    templatesById,
    fallback,
  });
}

export function courseIntervalLabel(
  step: StaffCourseStep,
  t: TFunction,
): string {
  if (step.sort_order <= 1 || !step.interval_value) {
    return t('treatment_courses.interval_first_step_hint');
  }
  const unitKey =
    step.interval_value === 1
      ? `treatment_courses.interval_unit_${step.interval_unit}_one`
      : `treatment_courses.interval_unit_${step.interval_unit}_other`;
  const toleranceKey =
    step.interval_tolerance_value === 1
      ? `treatment_courses.interval_unit_${step.interval_tolerance_unit}_one`
      : `treatment_courses.interval_unit_${step.interval_tolerance_unit}_other`;
  if (step.interval_tolerance_value > 0) {
    return t('treatment_courses.interval_summary', {
      count: step.interval_value,
      unit: t(unitKey),
      tolerance: step.interval_tolerance_value,
      toleranceUnit: t(toleranceKey),
    });
  }
  return t('treatment_courses.interval_summary_plain', {
    count: step.interval_value,
    unit: t(unitKey),
  });
}

export function courseStepWindow(
  steps: readonly StaffCourseStep[],
  step: StaffCourseStep,
): CourseIntervalWindow | null {
  if (step.sort_order <= 1) return null;
  return computeCourseIntervalWindow({
    previousStartsAt: previousBookedStartsAtForStep(steps, step.sort_order),
    intervalValue: step.interval_value,
    intervalUnit: step.interval_unit,
    toleranceValue: step.interval_tolerance_value,
    toleranceUnit: step.interval_tolerance_unit,
  });
}

/** Empty allow-list means unrestricted — same as today. */
export function isQuestionnaireAllowedOnStep(
  templateId: string,
  allowedIds: readonly string[] | null | undefined,
): boolean {
  if (!allowedIds || allowedIds.length === 0) return true;
  return allowedIds.includes(templateId);
}

/** Empty allow-list means unrestricted — same as today. */
export function filterAllowedQuestionnaires<T extends { id: string }>(
  templates: readonly T[],
  allowedIds: readonly string[] | null | undefined,
): T[] {
  return templates.filter((template) => isQuestionnaireAllowedOnStep(template.id, allowedIds));
}

export function nextCourseStep(steps: readonly StaffCourseStep[]): StaffCourseStep | null {
  const currentId = deriveCurrentStepId(steps);
  return steps.find((step) => step.id === currentId) ?? null;
}

export function coursePractitionerNames(steps: readonly StaffCourseStep[]): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  for (const step of steps) {
    for (const person of step.practitioners) {
      const name = resolveCoursePersonName(person.name);
      if (!name || seen.has(person.id)) continue;
      seen.add(person.id);
      names.push(name);
    }
  }
  return names;
}

export function coursePeriod(steps: readonly StaffCourseStep[]): {
  start: string | null;
  end: string | null;
} {
  const starts = steps
    .map((step) => step.starts_at)
    .filter((value): value is string => Boolean(value))
    .sort();
  return { start: starts[0] ?? null, end: starts[starts.length - 1] ?? null };
}

export function courseStepTitle(step: StaffCourseStep, fallback: string): string {
  return stepDisplayTitle(step, fallback);
}

export function courseIntervalAfterLabel(
  step: StaffCourseStep,
  previousSortOrder: number,
  t: TFunction,
): string {
  return `${courseIntervalLabel(step, t)} ${t('treatment_courses.interval_after_step', { n: previousSortOrder })}`;
}

export function questionnaireStatusLabel(
  state: 'planned' | 'sent' | 'completed' | 'failed' | 'skipped',
  t: TFunction,
): string {
  switch (state) {
    case 'completed':
      return t('treatment_courses.q_answered');
    case 'sent':
    case 'failed':
      return t('treatment_courses.q_state_sent');
    case 'planned':
      return t('treatment_courses.q_state_planned');
    case 'skipped':
      return t('treatment_courses.q_state_skipped');
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

export function questionnaireTimingLine(
  input: {
    phase: 'before_appointment' | 'after_appointment';
    offsetMinutes?: number | null;
    sentAt?: string | null;
    scheduledFor?: string | null;
  },
  t: TFunction,
): string {
  const phase =
    input.phase === 'after_appointment'
      ? t('treatment_courses.q_after')
      : t('treatment_courses.q_before');
  const minutes = Math.max(0, Number(input.offsetMinutes) || 0);
  const days = minutes > 0 ? Math.max(1, Math.round(minutes / (24 * 60))) : 0;
  const sent = Boolean(input.sentAt);
  const after = input.phase === 'after_appointment';
  if (days <= 0) {
    return sent
      ? t('treatment_courses.q_timing_sent_immediate', { phase })
      : t('treatment_courses.q_timing_planned_immediate', { phase });
  }
  if (sent) {
    return after
      ? t('treatment_courses.q_timing_sent_after', { phase, count: days })
      : t('treatment_courses.q_timing_sent_before', { phase, count: days });
  }
  return after
    ? t('treatment_courses.q_timing_sends_after', { phase, count: days })
    : t('treatment_courses.q_timing_sends_before', { phase, count: days });
}

export function suggestJournalTemplateTitle(
  stepTitle: string,
  titles: readonly string[],
): string | null {
  const needle = stepTitle.trim().toLowerCase();
  if (!titles.length) return null;
  if (!needle) return titles[0] ?? null;
  const match = titles.find((title) => {
    const value = title.trim().toLowerCase();
    return value && (value.includes(needle) || needle.includes(value));
  });
  return match ?? titles[0] ?? null;
}

export function courseActionTitle(item: CourseActionItem, t: TFunction): string {
  switch (item.kind) {
    case 'missing_journal':
      return t('treatment_courses.action_journal');
    case 'unanswered_questionnaire':
      return t('treatment_courses.action_questionnaire', {
        name: item.questionnaireName || t('treatment_courses.q_unnamed'),
      });
    case 'unbooked':
      return item.sortOrder <= 1
        ? t('treatment_courses.action_unbooked_first')
        : t('treatment_courses.action_unbooked');
    case 'outside_window':
      return t('treatment_courses.action_window');
    default: {
      const _exhaustive: never = item.kind;
      return _exhaustive;
    }
  }
}

export function courseListActionSummary(
  items: readonly CourseActionItem[],
  t: TFunction,
): string {
  const journal = items.find((item) => item.kind === 'missing_journal');
  const questionnaire = items.find((item) => item.kind === 'unanswered_questionnaire');
  if (journal && questionnaire) {
    return t('treatment_courses.list_action_summary', { n: journal.sortOrder });
  }
  const first = items[0];
  if (!first) return '';
  switch (first.kind) {
    case 'missing_journal':
      return t('treatment_courses.action_journal');
    case 'unanswered_questionnaire':
      return t('treatment_courses.action_questionnaire', {
        name: first.questionnaireName || t('treatment_courses.q_unnamed'),
      });
    case 'unbooked':
      return courseActionTitle(first, t);
    case 'outside_window':
      return t('treatment_courses.action_window');
    default: {
      const _exhaustive: never = first.kind;
      return _exhaustive;
    }
  }
}

export function courseActionItemMeta(
  item: CourseActionItem,
  step: StaffCourseStep | undefined,
  totalSteps: number,
  language: string,
  t: TFunction,
): string {
  const head = t('treatment_courses.step_of_total', {
    n: item.sortOrder,
    total: totalSteps,
  });
  switch (item.kind) {
    case 'unanswered_questionnaire': {
      const sentAt = step?.questionnaires.find(
        (row) => row.forms_template_id === item.questionnaireId,
      )?.sent_at;
      return sentAt
        ? `${head} · ${t('treatment_courses.action_sent_on', {
            when: formatCourseDate(sentAt, language),
          })}`
        : head;
    }
    case 'missing_journal': {
      const when = step?.starts_at ? formatCourseDateTime(step.starts_at, language) : '';
      const name = resolveCoursePersonName(step?.practitioners[0]?.name) ?? '';
      return [head, when, name].filter(Boolean).join(' · ');
    }
    case 'unbooked':
    case 'outside_window':
      return step?.starts_at
        ? `${head} · ${formatCourseDateTime(step.starts_at, language)}`
        : head;
    default: {
      const _exhaustive: never = item.kind;
      return _exhaustive;
    }
  }
}
